import { Inject, Injectable, Optional, inject } from "@angular/core";
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { Observable, firstValueFrom, map } from "rxjs";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Meta, Title } from "@angular/platform-browser";
import { REQUEST } from "@nguniversal/express-engine/tokens";
import { Request } from "express";
import { SeoService } from "src/app/services/seo.service";
import { buildSEOImageUrl } from "src/app/utils/seo-image.helper";


@Injectable({ providedIn: "root" })
export class IllustrationResolver implements Resolve<any> {
  afs = inject(AngularFirestore);
  constructor(
    private meta: Meta, // private socialService: SocialService,
    private title: Title,
    public router: Router,
    private seo: SeoService
  ) { }
  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<any> {
    const slug = route.paramMap.get("slug");
    const ref = this.afs.collection("filesMaster", (ref) =>
      ref.where("slug", "==", slug).orderBy("createdOn", "desc")
    );
    const data$ = ref.valueChanges().pipe(
      map((res: any[]) => {

        // new
        const illustration = res[0];
        if (!illustration) {
          this.router.navigate(["/"]);
          return null;
        }
        this.populateMetaTags(illustration);
        return illustration;

        // existing
        // this.populateMetaTags(res[0], route);
        // return res[0];
      })
    ); // Get single item

    return await firstValueFrom(data$);
  }

  /** 🔎 Collect related image URLs */
  // getRelatedImages(data: any, callback: (images: string[]) => void) {

  //   if (!data.relatedZ || !data.relatedZ.length) {
  //     callback([]);
  //     return;
  //   }

  //   const tag = data.relatedZ[0];
  //   const images: string[] = [];

  //   this.afs.collection("filesMaster", ref =>
  //     ref.where("relatedZ", "array-contains", tag)
  //       .where("status", "==", "Published")
  //       .limit(5)
  //   ).valueChanges().subscribe((items: any[]) => {

  //     items.forEach(item => {
  //       const img = item.previewUrl || item.jpeg || item.png;

  //       if (item.slug !== data.slug && img && !images.includes(img)) {
  //         images.push(img);
  //       }
  //     });

  //     callback(images);
  //   });
  // }

  populateMetaTags(data: any) {
    //console.log(route.paramMap);
    // if (!data.title) {
    //   this.router.navigate(["/"]);
    //   return;
    // }
    if (!data || !data.title) return;

    const title = data.title;
    const desc =
      data.desc ||
      "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.";
    const image = buildSEOImageUrl(data?.previewUrl || data.jpeg || data.png);
    const keywords = data.metadata
      ? data.metadata.join(", ")
      : "illusbook, illusbook.com, free illustrations, high-quality illustrations, professional illustrations, SaaS illustrations, fintech illustrations, HR tech illustrations, healthcare illustrations, marketing illustrations, education illustrations, tech product illustrations, app illustrations, website illustrations, UI illustrations, UX illustrations, dashboard illustrations, landing page illustrations, vector illustrations, illustration library, business illustrations, startup illustrations, creative assets for designers, ready-to-use illustrations, digital product illustrations, custom illustrations for SaaS, design resources for tech companies, modern flat illustrations, premium free illustrations, product UI visuals";
    //console.log(
    //   "Router URL:",
    //   "https://illusbook.com/illustration/" + data.slug
    // );
    //console.log("Router URL:", this.router);
    // const protocol =
    //   this.request.headers["x-forwarded-proto"] || this.request.protocol;
    // const host = this.request.headers.host;
    // const originalUrl = this.request.originalUrl;
    // const fullURL = `${protocol}://${host}${originalUrl}`;
    const fullURL = `https://illusbook.com/illustration/${data.slug}`;
    //console.log("Window URL:", fullURL);
    this.title.setTitle(`${title} | Illusbook`);
    this.setMetaTag("name", "title", title);
    this.setMetaTag("name", "description", desc);
    this.setMetaTag("name", "robots", "index, follow, max-image-preview:large");
    this.setMetaTag("name", "keywords", keywords);
    this.setMetaTag("name", "og:title", title);
    this.setMetaTag("property", "og:title", title);
    this.setMetaTag("property", "og:description", desc);
    this.setMetaTag("property", "og:image", image);
    this.setMetaTag("property", "og:image:width", "1200");
    this.setMetaTag("property", "og:image:height", "1200");
    this.setMetaTag("property", "og:image:type", "image/jpeg");
    this.setMetaTag("property", "og:url", fullURL);
    this.setMetaTag("property", "og:type", "website");
    this.setMetaTag("property", "og:site_name", "Illusbook");
    this.setMetaTag("name", "twitter:card", "summary_large_image");
    this.setMetaTag("name", "twitter:title", title);
    this.setMetaTag("name", "twitter:description", desc);
    this.setMetaTag("name", "twitter:image", image);

    const ORGANIZATION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://illusbook.com/#organization",
      "name": "Illusbook",
      "url": "https://illusbook.com",
      "logo": "https://illusbook.com/assets/favicon/android-icon-192x192.png",
      "description": "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@illusbook.com",
        "availableLanguage": ["English"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/illusbook/",
        "https://www.instagram.com/illusbook/",
        "https://x.com/illusbook",
        "https://www.pinterest.com/illusbook/"
      ]
    };

    const baseSchema: any = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": fullURL + "#webpage",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": fullURL + "#webpage"
      },
      url: fullURL,
      name: title,
      description: desc,
      image: image,
      primaryImageOfPage: {
        "@type": "ImageObject",
        contentUrl: image,
        url: image,
        representativeOfPage: true,
          width: 1200,
          height: 1200
      },
      "license": "https://illusbook.com/license",
      "acquireLicensePage": "https://illusbook.com/pricing",
      "creditText": "Designed by Illusbook",
      creator: {
        "@type": "Organization",
        name: "Illusbook",
      },
      "copyrightNotice": "Illusbook",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://illusbook.com" },
          { "@type": "ListItem", position: 2, name: "Illustration", item: fullURL }
        ]
      },
      publisher: { "@id": "https://illusbook.com/#organization" },
      inLanguage: "en",
      isPartOf: {
        "@type": "WebSite",
        "@id": "https://illusbook.com/#website"
      },
      datePublished: data.createdAt || new Date().toISOString(),
      dateModified: data.updatedAt || new Date().toISOString(),
    }

    // ---------------- 🔥 RELATED IMAGE ENGINE ----------------

    const tags = data.relatedZ || [];

    if (!tags.length) {
      // Inject schema without gallery
      this.seo.setJSONLD([baseSchema, ORGANIZATION_SCHEMA]);
      return;
    }


    this.afs.collection("filesMaster", ref =>
      ref.where("status", "==", "Published")
        .where("relatedZ", "array-contains-any", tags)
        .limit(8)
    ).valueChanges().subscribe((items: any[]) => {

      const gallery = items
        .filter(item => item.slug !== data.slug)
        .map(item => {
          const img = buildSEOImageUrl(item.previewUrl || item.jpeg || item.png);
          return {
            "@type": "ImageObject",
            contentUrl: img,
            thumbnailUrl: img,
            name: item.title
          };
        });

      if (gallery.length) {
        baseSchema.mainEntity = {
          "@type": "ImageGallery",
          associatedMedia: gallery
        };
      }

      // ---------------- JSON-LD (ONE clean injection) ----------------
      this.seo.setJSONLD([baseSchema,
        ORGANIZATION_SCHEMA
      ]);
    });
  }

  setMetaTag(
    attrName: "name" | "property",
    attrValue: string,
    content: string
  ) {
    const selector = `${attrName}="${attrValue}"`;
    //console.log(selector);
    this.meta.removeTag(selector);
    this.meta.addTag({ [attrName]: attrValue, content });
    // this.meta.updateTag({ [attrName]: attrValue, content });
  }
}
